import React, { useEffect, useRef, useState } from "react";
import ArrowDown from "../../../assets/arrow_down.svg";
import Choice from "../../../assets/choice.svg";
import { City, Country } from "../../../types/geo";
import { useSelectorsCitiesQuery, useSelectorsCountriesQuery } from "../../../hooks/useSelectorsQuery";
import Cross from '../../../assets/cross.svg';
import { LoadingMore } from "../../Loading";
import { formatDateToYYYYMMDD } from "../../../helpers/dates";
import { debouncedSearch } from "../../../helpers/text";
import { ColleagueForSelector } from "../../../types/users";
import { useMyColleaguesQuery, useUsersQuery } from "../../../hooks/useUsersQuery";
import { useClaimColleaguesQuery } from "../../../hooks/useTravelClaimQuery";
import { useChatAvailableParticipantsQuery } from "../../../hooks/useChatAvailableParticipantsQuery";

interface LocationSelectProps {
    label: string;
    location: string;
}

export const LocationSelect = ({ label, location }: LocationSelectProps) => (
    <div className="inline-flex flex-col items-start gap-2 relative flex-[0_0_auto]">
        <div className="relative w-[269px] mt-[-1.00px] text-[#949494] font-manager-acrom-ph-14-medium-acrom">
            {label}
        </div>
        <div
            className="inline-flex items-center px-4 py-3 relative flex-[0_0_auto] bg-[#f2f2f2] rounded-lg overflow-hidden">
            <div className="relative w-[239px] h-6 mr-[-2.00px]">
                <div className="absolute top-[3px] left-0 text-black font-manager-acrom-button4-16-medium-acrom">
                    {location}
                </div>
                <img alt="" src={ArrowDown} className="!absolute !w-6 !h-6 !top-0 !left-[213px]" />
            </div>
        </div>
    </div>
);


export const DateInput = () => (
    <div className="inline-flex flex-col items-start gap-2 relative flex-[0_0_auto]">
        <div className="relative w-[269px] mt-[-1.00px] text-[#949494] font-manager-acrom-ph-14-medium-acrom">
            Дата
        </div>
        <div
            className="inline-flex items-center gap-[174px] px-4 py-3 relative flex-[0_0_auto] bg-[#f2f2f2] rounded-lg overflow-hidden">
            <div className="relative w-[237px] h-6">
                <div className="inline-flex items-center absolute top-0.5 left-0">
                    <div className="relative w-fit mt-[-1.00px] font-manager-acrom-ph1-16-medium-acrom text-black">
                        01/05/2024
                    </div>
                </div>
                <div className="top-0 bg-[url(assets/calendar.svg)] bg-[100%_100%] absolute w-6 h-6 left-[213px]">
                    <div className="absolute w-px h-px top-3 left-1.5 bg-[#323232] rounded-[0.63px]" />
                    <div className="absolute w-px h-px top-4 left-1.5 bg-[#323232] rounded-[0.63px]" />
                    <div className="absolute w-px h-px top-3 left-[11px] bg-[#323232] rounded-[0.63px]" />
                    <div className="absolute w-px h-px top-4 left-[11px] bg-[#323232] rounded-[0.63px]" />
                    <div className="absolute w-px h-px top-3 left-4 bg-[#323232] rounded-[0.63px]" />
                </div>
            </div>
        </div>
    </div>
);


interface OptionSelectProps {
    label: string;
    options: string[];
}

interface TextareaProps {
    max?: number
    rows?: number
    readOnly?: boolean
    text: string;
    handleChange: any;
    placeholder?: string;
}

export const TextareaCount = ({ readOnly, text, handleChange, placeholder, max, rows }: TextareaProps) => {

    return (
        <div
            className="flex flex-col w-full items-start gap-2.5 px-4 py-3.5 relative flex-[0_0_auto] bg-[#f2f2f2] rounded-lg overflow-hidden">
            <textarea
                disabled={!!readOnly}
                rows={rows ?? 5}
                value={text}
                onChange={handleChange}
                placeholder={placeholder}
                className="
                resize-none
                outline-none
                bg-transparent
                w-full leading-[var(--client-acrom-ph1-16-medium-acrom-line-height)] relative mt-[-1.00px] font-client-acrom-ph1-16-medium-acrom font-[number:var(--client-acrom-ph1-16-medium-acrom-font-weight)] text-black text-[length:var(--client-acrom-ph1-16-medium-acrom-font-size)] tracking-[var(--client-acrom-ph1-16-medium-acrom-letter-spacing)] [font-style:var(--client-acrom-ph1-16-medium-acrom-font-style)]"
            />
            <div
                className="w-full text-[#949494] text-[length:var(--manager-acrom-ph-14-medium-acrom-font-size)] leading-[var(--manager-acrom-ph-14-medium-acrom-line-height)] relative font-manager-acrom-ph-14-medium-acrom font-[number:var(--manager-acrom-ph-14-medium-acrom-font-weight)] tracking-[var(--manager-acrom-ph-14-medium-acrom-letter-spacing)] [font-style:var(--manager-acrom-ph-14-medium-acrom-font-style)]">
                {text.length}/{max ?? 500}
            </div>
        </div>
    );
}

interface CheckboxProps {
    checked: boolean;
    onChange: (checked: boolean) => void;
    disabled?: boolean;
}

export const CheckboxChecked = () => (
    <div className="relative w-6 h-6">
        <div className="relative h-6 bg-[#fcd700] rounded-lg">
            <img alt="" src={Choice}
                className="!absolute !w-[13px] !h-[9px] !top-2 !left-1.5" />
        </div>
    </div>
);

export const CheckboxUnchecked = () => (<div className="relative w-6 h-6 bg-[#f2f2f2] rounded-lg" />);

export const CheckBox = ({ checked, onChange, disabled }: CheckboxProps) => {
    return (
        <label className="cursor-pointer">
            <input
                disabled={disabled}
                type="checkbox"
                className="hidden"
                checked={checked}
                onChange={(e) => onChange(e.target.checked)}
            />
            {checked ? <CheckboxChecked /> : <CheckboxUnchecked />}
        </label>
    );
}

interface DatepickerProps {
    readOnly?: boolean
    onDateChange: (date: string | null) => void;
    date?: string | null
}

export const Datepicker = ({ onDateChange, date, readOnly }: DatepickerProps) => {
    const [selectedDate, setSelectedDate] = useState<string | null>(date || null);
    const datepickerInputRef = useRef<HTMLInputElement | null>(null);
    const handleDateChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        let value = event.target.value
        if (value.length === 0) {
            setSelectedDate(null);
            onDateChange(null);

            return
        }
        const date = new Date(value);
        const dateString = formatDateToYYYYMMDD(date);
        setSelectedDate(dateString);
        onDateChange(dateString);
    };

    const handleInputClick = () => {
        if (!readOnly && datepickerInputRef.current) {
            datepickerInputRef.current.showPicker();
        }
    };

    return (
        <div className="relative w-full h-12 flex flex-row justify-between items-center">
            <input
                ref={datepickerInputRef}
                onClick={handleInputClick}
                type="date"
                value={date || ''}
                onChange={handleDateChange}
                className="
                    w-full
                    px-4 py-3
                    flex-[0_0_auto]
                    bg-[#f2f2f2]
                    rounded-lg
                    justify-between
                    items-center
                    h-12
                    focus:outline-none
                    border-transparent focus:border-transparent focus:ring-0
                "
            />
            <div className="absolute right-4 bg-[url(assets/calendar.svg)] bg-[100%_100%]  w-6 h-6"></div>
        </div>
    );
};


type Option = Country | City | ColleagueForSelector

interface SelectDropdownProps {
    readOnly?: boolean
    fetchDataFunc: typeof useSelectorsCountriesQuery | typeof useSelectorsCitiesQuery | typeof useMyColleaguesQuery | typeof useUsersQuery | typeof useClaimColleaguesQuery | typeof useChatAvailableParticipantsQuery
    renderItem?: (option: Option) => React.ReactNode
    placeholder?: string
    filterParam?: any
    selected?: Option | null
    onChange: (selectedValue: Option) => void;
}

// Определяем тип варианта в списке
export const SelectDropdown = ({
    fetchDataFunc,
    renderItem,
    selected,
    onChange,
    placeholder,
    readOnly,
    filterParam
}: SelectDropdownProps) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [searchText, setSearchText] = useState('');
    const [selectedOption, setSelectedOption] = useState<Option | null | undefined>(selected);
    const {
        data,
        fetchNextPage,
        hasNextPage,
        isFetchingNextPage,
        refetch
    } = fetchDataFunc(
        searchQuery,
        filterParam
    )


    // Ref на выпадающий список
    const dropdownRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        // Скролл
        const handleScroll = () => {
            if (dropdownRef.current) {
                const { scrollTop, scrollHeight, clientHeight } = dropdownRef.current;
                if (scrollTop + clientHeight >= scrollHeight - 5) {
                    if (hasNextPage && !isFetchingNextPage) {
                        fetchNextPage();
                    }
                }
            }
        };

        // Назначаем обработчик скролла выпадающему списку
        if (dropdownRef.current) {
            dropdownRef.current.addEventListener('scroll', handleScroll);
        }

        return () => {
            // Убираем обработчик скролла у выпадающего спика
            if (dropdownRef.current) {
                dropdownRef.current.removeEventListener('scroll', handleScroll);
            }
        };
    }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

    // Поиск в выпадающем списке START vvv

    const handleSearch = (value: string) => {
        setSearchQuery(value);
        refetch(); // Заново запрашиваем
    }
    useEffect(() => {
        refetch(); // Заново запрашиваем
    }, [searchQuery]);

    const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSearchText(event.target.value);
        debouncedSearch(event.target.value, handleSearch);
    };

    // Очистка поиска в выпадающем списке
    const handleSearchClear = () => {
        setSearchText('');
        handleSearch('')
    };

    // Выбор варианта в списке
    const handleOptionSelect = (option: Option) => {
        onChange(option);
        setSelectedOption(option);
        setIsOpen(false);
    };

    const renderSelectedItem = () => {
        if (selected) {
            return renderItem ? renderItem(selected) : selected.name
        }
        return placeholder
    }

    // Поиск в выпадающем списке END ^^^
    return (
        <div className="relative flex flex-col w-full items-center px-4 py-3  flex-[0_0_auto] bg-[#f2f2f2] rounded-lg">
            {/*Выбранный вариант*/}
            <div
                className="w-full cursor-pointer relative h-6"
                onClick={() => {
                    !readOnly && setIsOpen(!isOpen)
                }}
            >
                <div
                    className="w-11/12 absolute top-[3px] left-0 text-black truncate ... text-[length:var(--manager-acrom-button4-16-medium-acrom-font-size)] leading-[var(--manager-acrom-button4-16-medium-acrom-line-height)] whitespace-nowrap font-manager-acrom-button4-16-medium-acrom font-[number:var(--manager-acrom-button4-16-medium-acrom-font-weight)] tracking-[var(--manager-acrom-button4-16-medium-acrom-letter-spacing)] [font-style:var(--manager-acrom-button4-16-medium-acrom-font-style)]">
                    {renderSelectedItem()}
                </div>
                <img alt="" src={ArrowDown} className="absolute !w-6 !h-6 !top-0 !right-0" />
            </div>
            {/*Контейнер со списком вариантов*/}
            <div
                className={
                    " absolute top-16 left-0 flex flex-col w-full p-2 bg-[#f2f2f2] shadow-2xl rounded-lg z-10 "
                    + (isOpen ? "" : "hidden")
                }
            >
                {/*Поиск*/}
                <div className="relative flex flex-row w-full">
                    <input
                        className="w-full bg-none mb-2 p-2 rounded-lg outline-none focus:outline-none"
                        type="text"
                        placeholder="Поиск..."
                        value={searchText}
                        onChange={handleSearchChange}
                    />
                    {searchQuery && <img
                        onClick={handleSearchClear}
                        alt=""
                        src={Cross}
                        className="cursor-pointer absolute !w-6 !h-6 !top-2 !right-2"
                    />}
                </div>
                {/*Список вариантов*/}
                <div className="overflow-y-auto h-48 w-full" ref={dropdownRef}>
                    {data?.pages.map((page, i) => (
                        <React.Fragment key={i}>
                            {
                                page
                                    .filter((option) => option.name.length > 0)
                                    .map((option: any) => (
                                        <div
                                            className="cursor-pointer p-1"
                                            key={option.pk} onClick={() => handleOptionSelect(option)}>
                                            {/*Отрисовка на стороне вызывающей функции*/}
                                            {renderItem ? renderItem(option) : option.name}
                                        </div>
                                    ))
                            }
                        </React.Fragment>
                    ))}
                    {/*Индикатор подгрузки*/}
                    {isFetchingNextPage && <LoadingMore />}
                </div>
            </div>
        </div>
    );
};
